import { GraphQLService } from '@affine/core/modules/cloud';
import { WorkspaceRealtimeService } from '@affine/core/modules/editor';
import { onBackendAvailable } from '@affine/core/utils/backend-availability';
import { customAppAppsQuery } from '@affine/graphql';
import type { CustomAppKind } from '@streamlain/view-config';
import { useService } from '@toeverything/infra';
import type { GraphQLError } from 'graphql';
import { useEffect } from 'react';
import type { SWRResponse } from 'swr';
import useSWR from 'swr';

import type { AppResourceAreaRef } from '../app-resource-area-ui';
import {
  FEATURE_KEY_CUSTOM_APPS_CREATE,
  useFeatureGate,
} from './use-feature-gate';

export interface CustomAppLaunchAccess {
  schemaVersion: 1;
  customAppId: string;
  workspaceId: string;
  rootDocId: string;
  title: string;
  slug: string;
  isPublished: boolean;
  publishedAt: string | null;
  updatedAt: string;
  lifecycleState: 'active' | 'deleted' | 'trashed';
  trashedAt: string | null;
  deletedAt: string | null;
  area: AppResourceAreaRef;
  appId: string | null;
  productMode: 'custom-app';
  appKind: CustomAppKind | null;
  launchPath: string;
  launchable: boolean;
  canEdit: boolean;
  canManage: boolean;
  appRole: 'none' | 'reader' | 'writer' | 'admin';
  effectiveRootRole: string;
  reason: string;
  previewSet?: unknown;
}

interface CustomAppAppsPayload {
  schemaVersion: 1;
  apps: CustomAppLaunchAccess[];
}

export interface UseWorkspaceLaunchableCustomAppsResult {
  apps: CustomAppLaunchAccess[] | undefined;
  isLoading: boolean;
  error: GraphQLError | undefined;
  mutate: SWRResponse<CustomAppLaunchAccess[], GraphQLError>['mutate'];
}

export interface UseWorkspaceLaunchableCustomAppResult {
  app: CustomAppLaunchAccess | null | undefined;
  isLoading: boolean;
  error: GraphQLError | undefined;
  mutate: UseWorkspaceLaunchableCustomAppsResult['mutate'];
}

export interface CustomAppLaunchProjectionUpdate {
  id: string;
  slug: string;
  title: string;
  isPublished: boolean;
  updatedAt: string;
}

export function applyCustomAppLaunchProjectionUpdate(
  apps: CustomAppLaunchAccess[] | undefined,
  update: CustomAppLaunchProjectionUpdate
): CustomAppLaunchAccess[] | undefined {
  if (!apps) return apps;
  return apps.map(app =>
    app.customAppId === update.id
      ? {
          ...app,
          slug: update.slug,
          title: update.title,
          isPublished: update.isPublished,
          updatedAt: update.updatedAt,
        }
      : app
  );
}

export function useWorkspaceLaunchableCustomApps(
  workspaceId: string | null
): UseWorkspaceLaunchableCustomAppsResult {
  const graphql = useService(GraphQLService);
  const workspaceRealtimeService = useService(WorkspaceRealtimeService);
  const {
    isFeatureAllowed,
    isLoading: isFeatureGateLoading,
    isLocalEntitlementReady,
  } = useFeatureGate();
  const localEntitlementReady =
    isLocalEntitlementReady ?? !isFeatureGateLoading;
  const canQueryLaunchableCustomApps =
    localEntitlementReady && isFeatureAllowed(FEATURE_KEY_CUSTOM_APPS_CREATE);

  const { data, error, isLoading, mutate } = useSWR<
    CustomAppLaunchAccess[],
    GraphQLError
  >(
    workspaceId && canQueryLaunchableCustomApps
      ? ['customAppLaunches', workspaceId]
      : null,
    async () => {
      const res = await graphql.gql({
        query: customAppAppsQuery,
        variables: { workspaceId: workspaceId as string },
      });
      const payload = res.customAppApps as CustomAppAppsPayload;
      return payload.apps;
    },
    {
      suspense: false,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      shouldRetryOnError: error => !isFeatureUnavailableError(error),
      // One bounded retry is enough for the backend pool to replace a stale
      // shard client. Further attempts require explicit user intent.
      errorRetryCount: 1,
      errorRetryInterval: 1000,
    }
  );

  useEffect(() => {
    if (!workspaceId || !canQueryLaunchableCustomApps) return;
    return onBackendAvailable(() => {
      void mutate();
    });
  }, [canQueryLaunchableCustomApps, mutate, workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;
    const releaseWorkspacePresence =
      workspaceRealtimeService.retainWorkspacePresence();
    const subscription =
      workspaceRealtimeService.workspaceMetaChanged$.subscribe(event => {
        if (
          event &&
          event.workspaceId === workspaceId &&
          event.changeType.startsWith('custom_app_')
        ) {
          void mutate();
        }
      });
    return () => {
      subscription.unsubscribe();
      releaseWorkspacePresence();
    };
  }, [mutate, workspaceId, workspaceRealtimeService]);

  return {
    apps: data,
    isLoading: canQueryLaunchableCustomApps
      ? isLoading
      : !localEntitlementReady,
    error: isFeatureUnavailableError(error) ? undefined : error,
    mutate,
  };
}

export function useWorkspaceLaunchableCustomApp(
  workspaceId: string | null,
  customAppId: string | null
): UseWorkspaceLaunchableCustomAppResult {
  const { apps, isLoading, error, mutate } =
    useWorkspaceLaunchableCustomApps(workspaceId);

  if (isLoading) {
    return { app: undefined, isLoading: true, error, mutate };
  }
  if (apps === undefined) {
    return { app: undefined, isLoading: false, error, mutate };
  }
  if (!customAppId) {
    return { app: null, isLoading: false, error, mutate };
  }
  return {
    app: apps.find(app => app.customAppId === customAppId) ?? null,
    isLoading: false,
    error,
    mutate,
  };
}

function isFeatureUnavailableError(error: unknown): boolean {
  if (!error) return false;
  const code = (error as { extensions?: { code?: unknown } }).extensions?.code;
  if (code === 'FEATURE_NOT_AVAILABLE') return true;

  const graphQLErrors = (
    error as
      | {
          graphQLErrors?: Array<{
            extensions?: { code?: unknown };
          }>;
        }
      | undefined
  )?.graphQLErrors;
  return (
    graphQLErrors?.some(
      graphQLError => graphQLError.extensions?.code === 'FEATURE_NOT_AVAILABLE'
    ) ?? false
  );
}
